import { inject, injectable } from 'inversify'
import { Group } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { setup } from 'xstate'
import type { IPoint2 } from '@/plugins/cad/module/common/interface'
import { TYPES } from '@/plugins/cad/module/container/types'
import type {
  KeyEventInfoHandler,
  KeyEventParams,
  MouseEventInfoHandler,
  MouseEventParams,
} from '@/plugins/cad/module/core/common/event'
import type { DetectorEventHandler } from '@/plugins/cad/module/core/selector/Detector'
import { SelectMode, type SelectorEventHandler } from '@/plugins/cad/module/core/selector/Selector'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { KeyCodes } from '@/shared/constants'
import type { AuxiliaryMouseHelper } from '../auxiliaryTool/auxiliaryMouseHelper'
import { type MouseTipHelper, MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import type { CADController } from '../CADController'
import { BreakEntityCommand } from '../command/BreakEntityCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import type { IEntity } from '../model/entity'
import { EntityObjectUtils } from '../object/entityObject/entityObjectUtils'
import { createCadLine2Object } from '../object/material/CadLineFactory'
import type { EntitySelector } from '../selector/entitySelector'
import { EntityEditHandler } from './EntityEditHandler'
import { BreakUtils } from './utils/breakUtils'

const breakEntityMachine = setup({
  actions: {
    entrySpecifyFirstPoint: () => {},
    entrySpecifySecondPoint: () => {},
  },
}).createMachine({
  id: 'breakEntity',
  initial: 'idle',
  states: {
    idle: {
      on: { NEXT: 'specifyFirstPoint' },
    },
    specifyFirstPoint: {
      entry: { type: 'entrySpecifyFirstPoint' },
      on: { NEXT: 'specifySecondPoint' },
    },
    specifySecondPoint: {
      entry: { type: 'entrySpecifySecondPoint' },
    },
  },
})

/**
 * 打断实体工具：选中一个实体后指定两个点，将其按点位置切分。
 */
@injectable()
export class BreakEntityTool
  extends StateMachineTool<typeof breakEntityMachine>
  implements
    MouseEventInfoHandler,
    KeyEventInfoHandler,
    SelectorEventHandler<IEntity>,
    DetectorEventHandler<IEntity>
{
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.BreakEntityTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  protected machine = breakEntityMachine.provide({
    actions: {
      entrySpecifyFirstPoint: () => this.entrySpecifyFirstPoint(),
      entrySpecifySecondPoint: () => this.entrySpecifySecondPoint(),
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private selectedEntity?: IEntity
  private selectedEntityObject?: Line2
  private selectedEntityOriginalMaterial?: Line2['material']
  private firstPoint?: IPoint2
  private secondPoint?: IPoint2

  private group: Group = new Group()
  private previewLineObjects: Line2[] = []

  protected onInitialize(): void {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.entitySelector.config({ enableFrameSelect: false, mode: SelectMode.replace }) // 关闭框选功能，仅支持同时选中一个图元
    this.historyManager.setLock(true)
    this.inject(this.auxiliaryMouseHelper)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.group.parent?.remove(this.group)
    this.clearPreviewObjects()
    this.entitySelector.clearSelection()
    this.entitySelector.resetConfig()
    this.mouseTipHelper.removeTip(MouseTipId.Edit)
    this.historyManager.setLock(false)
    this.eject(this.auxiliaryMouseHelper)
    this.eject(this.entitySelector)
    this.selectedEntity = undefined
    this.selectedEntityObject = undefined
    this.selectedEntityOriginalMaterial = undefined
    this.firstPoint = undefined
    this.secondPoint = undefined
  }

  /**
   * 鼠标按下：依次指定第一点与第二点，第二点确定后执行打断。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button !== 0) return

    if (this.checkState('specifyFirstPoint') && this.selectedEntity) {
      // 指定第一个点（有实体才有效）
      this.firstPoint = this.getCapturedPoint(params)
      this.send({ type: 'NEXT' })
      return
    }

    if (this.checkState('specifySecondPoint')) {
      this.secondPoint = this.getCapturedPoint(params)
      this.executeOperate()
      this.exit()
    }
  }

  /**
   * 鼠标移动：指定第二点阶段刷新预览。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    if (!this.checkState('specifySecondPoint') || !this.firstPoint) return
    this.secondPoint = this.getCapturedPoint(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  /**
   * 选择变化回调：记录当前打断目标实体。
   *
   * @param entities - 当前已选中的实体
   */
  onSelectorChanged(entities: IEntity[]) {
    if (this.checkState('specifyFirstPoint') && entities.length > 0) {
      this.selectedEntity = entities[0]
      this.selectedEntityObject = this.viewManager.getEntityObject(this.selectedEntity) as Line2
      this.selectedEntityOriginalMaterial = this.selectedEntityObject.material
    }
  }

  /**
   * 实体探测过滤：仅保留支持打断的类型。
   *
   * @param entities - 候选实体列表
   * @returns 过滤后可打断的实体列表
   */
  onDetectorFilter(entities: IEntity[]) {
    return entities.filter((entity) => BreakUtils.EnabledEntityTypes.has(entity.type))
  }

  /************ 状态机动作 ************/
  private entrySpecifyFirstPoint() {
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.break.target)
    this.inject(this.entitySelector)
  }

  private entrySpecifySecondPoint() {
    this.eject(this.entitySelector)
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.break.secondPoint)
  }

  private getCapturedPoint(params: MouseEventParams, startPoint?: IPoint2) {
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })
    return { x: finalPoint.x, y: finalPoint.y }
  }

  /************ 预览 ************/
  private updateCanvas() {
    if (!this.selectedEntity || !this.selectedEntityObject || !this.firstPoint || !this.secondPoint)
      return
    const res = BreakUtils.breakEntity(this.selectedEntity, {
      firstPoint: this.firstPoint,
      secondPoint: this.secondPoint,
    })
    // 更新预览
    if (res.deleteEntities.length > 0) {
      // 锁定原实体
      this.selectedEntityObject.material = this.viewManager.objectManager
        .getMaterialManager()
        .getCachedLockedMaterial(this.selectedEntity)
    }
    // 显示预览实体
    res.createdEntities.forEach((entity, i) => {
      let previewObject = this.previewLineObjects[i]
      const geometry = EntityObjectUtils.createEntityLineGeometry(entity)
      if (!geometry) {
        previewObject && (previewObject.visible = false)
        return
      }
      if (!previewObject) {
        previewObject = createCadLine2Object(geometry, this.selectedEntityOriginalMaterial!)
        previewObject.computeLineDistances()
        this.group.add(previewObject)
        this.previewLineObjects[i] = previewObject
      } else {
        previewObject.geometry.dispose()
        previewObject.geometry = geometry
        previewObject.computeLineDistances()
        previewObject.visible = true
      }
    })
    this.previewLineObjects
      .slice(res.createdEntities.length)
      .forEach((obj) => (obj.visible = false))
  }

  private clearPreviewObjects() {
    this.previewLineObjects.forEach((obj) => {
      obj.geometry.dispose()
      this.group.remove(obj)
    })
    this.previewLineObjects = []
  }

  /************ 执行打断 ************/
  private executeOperate() {
    if (!this.firstPoint || !this.secondPoint || !this.selectedEntity) return
    const { createdEntities, deleteEntities } = EntityEditHandler.break([this.selectedEntity], {
      firstPoint: this.firstPoint,
      secondPoint: this.secondPoint,
    })
    if (createdEntities.length === 0 && deleteEntities.length === 0) return // 没有打断成功，无需执行命令
    this.historyManager.addCommandAndExecute(
      new BreakEntityCommand([this.selectedEntity], createdEntities, this.viewManager)
    )
  }
}
